import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { UserMealApi } from "../api/UserMealApi";
import { logger } from "../utils/logger";
import { getErrorMessage } from "../utils/errorMessages";
import { toast } from "sonner";

const MealsContext = createContext();

export const useMeals = () => {
  return useContext(MealsContext);
};

export const MealsProvider = ({ children }) => {
  const [meals, setMeals] = useState(null);

  // Fetch All Meals
  const fetchMeals = useCallback(async () => {
    try {
      const response = await UserMealApi.fetchAll();
      setMeals(response.userMeals);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to fetch meals", error, { correlationId });

      toast.error(errorMessage);
      throw error;
    }
  }, []);

  // Fetch Meal By ID
  const fetchMealById = async (id) => {
    try {
      const response = await UserMealApi.fetchById(id);
      return response;
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to fetch meal by ID", error, {
        correlationId,
        mealId: id,
      });

      if (error.response?.status !== 409) {
        logger.error("Failed to fetch meals", error, { correlationId });
      }

      toast.error(errorMessage);
      throw error;
    }
  };

  // Create Meal
  const createMeal = async (newMealInfo) => {
    try {
      const response = await UserMealApi.createMeal(newMealInfo);
      setMeals((prevMeals) => [...prevMeals, response]);
      return response;
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to create meal", error, { correlationId });

      toast.error(errorMessage);
      throw error;
    }
  };

  // Replace Meal
  const replaceMeal = async (mealId, mealData) => {
    const index = meals.findIndex((meal) => meal.mealId === mealId);

    try {
      const response = await UserMealApi.replaceById(mealId, mealData);
      setMeals((prev) => {
        const updated = [...prev];
        updated[index] = response;
        return updated;
      });
      return response;
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to replace meal", error, { correlationId, mealId });

      toast.error(errorMessage);

      throw error;
    }
  };

  // Remove Meal
  // UserMealsContext.js
  const removeMeal = async (id) => {
    // Added async
    const index = meals.findIndex((meal) => meal.mealId === id);
    const removedMeal = meals[index];

    // Optimistic UI Update
    setMeals((prev) => prev.filter((meal) => meal.mealId !== id));

    try {
      await UserMealApi.deleteById(id); // Use await here
    } catch (error) {
      // Rollback logic
      setMeals((prev) => {
        const updated = [...prev];
        updated.splice(index, 0, removedMeal);
        return updated;
      });

      // RE-THROW the error so the component can catch it!
      throw error;
    }
  };

  const value = {
    meals,
    removeMeal,
    createMeal,
    replaceMeal,
    fetchMealById,
    fetchMeals,
  };

  useEffect(() => {
    fetchMeals();
  }, [fetchMeals]);

  return (
    <MealsContext.Provider value={value}>{children}</MealsContext.Provider>
  );
};
